// store/modules/paymentSlips/late.js
import enums from '@/utils/enums';
import { formatMoney } from '@/utils/stringUtils';
import requestService from '@/services/request';
import { createTrace } from '@/services/logTrace';
import { getSolicitationLogEvents } from '../../../utils/logTraceEnums';
import {
  CONFIG,
  NOTIFICATIONS,
  ROLES,
  SOLICITATE_TYPE,
  PARCEL_TYPES,
  USER,
  UNIT,
} from '../../../utils/constants';
import moment from 'moment-timezone';

const state = {
  hasLateSlips: false,
  hasLateError: false,
  loadingItems: false,
  loadingSolicitations: false,
  lateError: null,
  lateSlipsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },

  hasAnticipationSlips: false,
  anticipationSlipsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },
  inDelaySolicitationAllowed: false,
  hasLateSolicitationsReady: false,
  hasAnticipationSolicitationsReady: false,
  lateSolicitationsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },
  anticipationSolicitationsCache: {
    unitKey: null,
    validUntil: null,
    data: null,
  },
  discountCampaign: {
    hasDiscountCampaign: false,
    allowedParcelTypes: [],
    allowedFrequencyTypes: [],
    releaseProRata: false,
  },

  lateSolicitationsPromise: null,
  latePaymentSlipsPromise: null,
  anticipationPaymentSlipsPromise: null,
  anticipationSolicitationsPromise: null,
  discountCampaignPromise: null,
  discountCampaignPromiseKey: null,
  selectedUnitKey: null,
};

const getters = {
  loadingItems: (state) => state.loadingItems,
  inDelaySolicitationAllowed: (state) => state.inDelaySolicitationAllowed,
  hasLateSolicitationsReady: (state) => state.hasLateSolicitationsReady,
  hasLateSlips: (state) => state.hasLateSlips,
  hasLateError: (state) => state.hasLateError,

  lateError: (state) => state.lateError,
  hasAnticipationSlips: (state) => state.hasAnticipationSlips,

  latePositionData: (state) => state?.lateSlipsCache?.data?.items || {},
  lateSolicitations: (state) =>
    state?.lateSolicitationsCache?.data?.items || [],

  anticipationPositionData: (state) =>
    state?.anticipationSlipsCache?.data?.items || {},
  anticipationSolicitations: (state) =>
    state?.anticipationSolicitationsCache?.data?.items || [],

  unitFinancialPositions(state, getters) {
    return {
      anticipation: getters.anticipationPositionData,
      late: getters.latePositionData,
    };
  },
  discountCampaign(state) {
    return state.discountCampaign;
  },
};

const mutations = {
  SET_LOADING(state, value) {
    state.loadingItems = value;
  },
  SET_LOADING_SOLICITATIONS(state, isLoading) {
    state.loadingSolicitations = isLoading;
  },
  SET_HAS_LATE_SLIPS(state, value) {
    state.hasLateSlips = value;
  },
  SET_HAS_ANTICIPATION_SLIPS(state, value) {
    state.hasAnticipationSlips = value;
  },
  SET_HAS_LATE_ERROR(state, value) {
    state.hasLateError = value;
  },
  SET_DELAY_ALLOWED(state, value) {
    state.inDelaySolicitationAllowed = value;
  },
  SET_LATE_SOLICITATIONS_READY(state, value) {
    state.hasLateSolicitationsReady = value;
  },
  SET_LATE_SOLICITATIONS_CACHE(state, { unitKey, validUntil, data }) {
    state.lateSolicitationsCache = { unitKey, validUntil, data };
  },
  CLEAR_LATE_SOLICITATIONS_CACHE(state) {
    state.lateSolicitationsCache.validUntil = null;
  },
  SET_LATE_SLIPS_CACHE(state, { unitKey, validUntil, data }) {
    state.lateSlipsCache = { unitKey, validUntil, data };
  },
  CLEAR_LATE_SLIPS_CACHE(state) {
    state.lateSlipsCache.validUntil = null;
  },

  SET_ANTICIPATION_SOLICITATIONS_READY(state, value) {
    state.hasAnticipationSolicitationsReady = value;
  },
  SET_ANTICIPATION_SOLICITATIONS_CACHE(state, { unitKey, validUntil, data }) {
    state.anticipationSolicitationsCache = { unitKey, validUntil, data };
  },
  CLEAR_ANTICIPATION_SOLICITATIONS_CACHE(state) {
    state.anticipationSolicitationsCache.validUntil = null;
  },
  SET_ANTICIPATION_SLIPS_CACHE(state, { unitKey, validUntil, data }) {
    state.anticipationSlipsCache = { unitKey, validUntil, data };
  },
  CLEAR_ANTICIPATION_SLIPS_CACHE(state) {
    state.anticipationSlipsCache.validUntil = null;
  },

  SET_DISCOUNT_CAMPAIGN(state, campaign) {
    state.discountCampaign = campaign;
  },
};

const actions = {
  getAllowedContracts({ rootGetters }, { module, contracts }) {
    const navigationRules = rootGetters[CONFIG.GET_NAVIGATION_RULES];
    const notAllowedContracts =
      navigationRules?.financial?.notAllowedContracts || {};
    const notAlloweds = notAllowedContracts[module] || [];

    return contracts.filter((contract) => !notAlloweds.includes(contract.type));
  },
  async getLateSolicitations(
    { commit, rootState, state, rootGetters, dispatch },
    contracts = null
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const unitKey = selectedUnit ? selectedUnit.unit.id : null;

    if (state.loadingSolicitations && state.lateSolicitationsPromise) {
      if (state.selectedUnitKey === unitKey) {
        // Mesma unidade, retorna a Promise pendente
        return await state.lateSolicitationsPromise;
      }
      // Unidade mudou, ignora a Promise antiga e cria nova
    }

    if (
      state.lateSolicitationsCache.unitKey === unitKey &&
      state.lateSolicitationsCache.validUntil &&
      moment().isBefore(state.lateSolicitationsCache.validUntil)
    ) {
      return state.lateSolicitationsCache.data.items;
    }

    state.selectedUnitKey = unitKey;
    state.lateSolicitationsPromise = (async () => {
      try {
        if (rootGetters[USER.GET_USER_ROLE] !== ROLES.CLIENT_ROLE) return;

        commit('SET_LOADING_SOLICITATIONS', true);

        if (!contracts) {
          if (!selectedUnit) return;
          const { LATE_SLIPS } = enums.financialModules;
          contracts = await dispatch('getAllowedContracts', {
            module: LATE_SLIPS,
            contracts: selectedUnit.contracts,
          });
        }

        const { sku } = selectedUnit;
        const params = {
          productSku: sku,
          contractIds: contracts.map((o) => o.id),
          contractTypes: contracts.map((o) => o.type),
          solicitationType: 'Late',
        };
        const url = `${process.env.FINANCIAL_API_URL}/solicitation/query`;

        const { items, solicitationAllowed } = await requestService(
          url,
          'GET',
          {
            params,
            localErrorHandling: true,
          }
        );

        const logSolicitations = [];
        const readySolicitations = items.reduce((ready, o) => {
          if (o.ready) {
            ready.push(o.items[0]);
          }
          if (o.logError || o.logGenerated || o.logExpired) {
            logSolicitations.push(o);
          }
          return ready;
        }, []);

        dispatch('logSolicitations', {
          logSolicitations,
          selectedUnit,
          solicitationType: 'late',
        });

        // Se a unidade mudou durante a requisição, ignora atualização
        if (state.selectedUnitKey !== unitKey) {
          return;
        }

        commit('SET_DELAY_ALLOWED', solicitationAllowed);

        if (readySolicitations.length > 0) {
          commit(NOTIFICATIONS.MUTATE_LATE_PAYMENT_SLIPS, readySolicitations, {
            root: true,
          });
          commit(
            'SET_LATE_SOLICITATIONS_READY',
            readySolicitations.some(
              (item) => item.solicitationType === SOLICITATE_TYPE.LATE
            )
          );
        }

        const cacheValidUntil = moment().add(2, 'minutes').toISOString();

        commit('SET_LATE_SOLICITATIONS_CACHE', {
          unitKey,
          validUntil: cacheValidUntil,
          data: { items, solicitationAllowed },
        });

        return items;
      } finally {
        commit('SET_LOADING_SOLICITATIONS', false);
        state.lateSolicitationsPromise = null;
      }
    })();

    return await state.lateSolicitationsPromise;
  },

  async logSolicitations(
    { dispatch },
    { logSolicitations, selectedUnit, solicitationType }
  ) {
    let lastContract = null;
    let title = null;
    for (const solicitation of logSolicitations) {
      const slipContract = selectedUnit.contracts.find(
        (contract) => contract.id === solicitation.contractId
      );
      const solicitationDetails = solicitation.items[0];
      if (slipContract && solicitationDetails) {
        if (lastContract != solicitation.contractId) {
          title = await dispatch(UNIT.GET_CONTRACT_TITLE, slipContract, {
            root: true,
          });
          lastContract = solicitation.contractId;
        }
        const { paymentTotal, solicitationCode, expirationDate, parcelIds } =
          solicitationDetails;

        const traceInfo = {
          contractId: slipContract.id,
          contractType: slipContract.type,
          detachedType: title,
          paymentTotal,
          solicitationCode,
          expirationDate,
          parcels: parcelIds,
        };

        const solicitationLogEvents = getSolicitationLogEvents(
          solicitationDetails.solicitationType || solicitationType
        );

        Object.keys(solicitationLogEvents).forEach((logType) => {
          if (!solicitationLogEvents[logType]) {
            console.error('Error trying to create solicitation log', logType);
            createTrace(enums.logTraceActions.controlledLogError, {
              Errors: {
                description: 'Error trying to create solicitation log',
                logType,
                traceInfo,
              },
            });
          } else if (solicitation[logType]) {
            createTrace(solicitationLogEvents[logType], traceInfo);
          }
        });
      }
    }
  },

  async getLatePaymentSlips(
    { commit, rootState, state, getters, rootGetters, dispatch },
    [contracts, useLoading = true]
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const unitKey = selectedUnit ? selectedUnit.unit.id : null;

    if (
      state.lateSlipsCache.unitKey === unitKey &&
      state.lateSlipsCache.validUntil &&
      moment().isBefore(state.lateSlipsCache.validUntil)
    ) {
      return getters.latePositionData;
    }

    if (
      state.loadingItems &&
      state.latePaymentSlipsPromise &&
      state.selectedUnitKey === unitKey
    ) {
      return await state.latePaymentSlipsPromise;
    }

    state.selectedUnitKey = unitKey;
    state.latePaymentSlipsPromise = (async () => {
      try {
        if (useLoading) commit('SET_LOADING', true);
        if (!contracts) {
          if (!selectedUnit) return;
          const { LATE_SLIPS } = enums.financialModules;
          contracts = await dispatch('getAllowedContracts', {
            module: LATE_SLIPS,
            contracts: selectedUnit.contracts,
          });
        }

        commit('SET_HAS_LATE_SLIPS', false);

        const url = `${process.env.FINANCIAL_API_URL}/late`;

        const lateSlipsByContract = {};

        const promises = contracts.map((contract) => {
          const params = {
            contractId: contract.id,
            contractType: contract.type,
          };
          lateSlipsByContract[contract.id] = null;
          return requestService(url, 'GET', {
            params,
            localErrorHandling: true,
          }).then((res) => {
            if (lateSlipsByContract[contract.id] === null)
              lateSlipsByContract[contract.id] = [];

            if (res && res.items.length > 0) {
              const parcels = res.items.map((p) => ({
                contractId: contract.id,
                ...p,
              }));
              lateSlipsByContract[contract.id] = parcels;

              if (!state.hasLateSlips) commit('SET_HAS_LATE_SLIPS', true);
            }
            return res;
          });
        });

        await Promise.all(promises);

        // Validação de parcel types
        for (const contract of contracts) {
          const index = lateSlipsByContract[contract.id].findIndex(
            (slip) => slip.type === PARCEL_TYPES.proSolutoPos
          );

          if (index > -1) {
            const validSlips = lateSlipsByContract[contract.id].filter(
              (slip) => slip.type !== PARCEL_TYPES.proSolutoPos
            );

            if (validSlips.length === 0) {
              commit('SET_HAS_LATE_ERROR', 'parcelTypesNotValid');
            } else {
              lateSlipsByContract[contract.id] = validSlips;
            }
          }
        }

        const cacheValidUntil = moment().add(2, 'minutes').toISOString();
        const newCache = {
          unitKey,
          validUntil: cacheValidUntil,
          data: { items: { ...lateSlipsByContract } },
        };
        commit('SET_LATE_SLIPS_CACHE', newCache);

        return lateSlipsByContract;
      } catch (err) {
        console.error('Error fetching late payment slips:', err);
        throw err;
      } finally {
        commit('SET_LOADING', false);
        state.latePaymentSlipsPromise = null;
      }
    })();

    return await state.latePaymentSlipsPromise;
  },

  resetLateSlipsCache({ commit, dispatch }) {
    commit('CLEAR_LATE_SLIPS_CACHE');
    commit('CLEAR_LATE_SOLICITATIONS_CACHE');
  },

  async sendSlipByEmail(
    { rootState },
    { item, email, mode, fromHome = false }
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const contractId = item.contractId;

    let solicitationType = null;
    switch (mode) {
      case 'anticipation':
        solicitationType = 'anticipation';
        break;
      case 'late':
        solicitationType = 'late';
        break;
      case 'toExpire':
        solicitationType = 'toExpire';
        break;
    }

    if (!solicitationType) {
      throw new Error('Invalid Email type');
    }

    const data = {
      contractId,
      productCode: selectedUnit.product.code,
      blockCode: selectedUnit.block.code,
      unitCode: selectedUnit.unit.code,
      documentCode: item.documentCode,
      solicitationType,
      userEmail: email,
    };

    const url = process.env.SLIP_CHECK_FUNCTION;
    const res = await requestService(`${url}/sendSlipEmail`, 'POST', {
      data,
      useLoading: false,
      localErrorHandling: true,
    });

    const { emailSended } = res;
    if (emailSended) {
      const slipContract = selectedUnit.contracts.find(
        (contract) => contract.id === contractId
      );
      const traceInfo = {
        contractId: contractId,
        contractType: slipContract.type,
        detachedType: slipContract.detachedType,
        expirationDate: item.expirationDate,
        serieId: item.serieId,
        serieCode: item.serieCode,
        parcelCode: item.parcelCode,
        discountedValue: item.discountedValue,
        ready: item.ready,
        url: item.slipUrl,
      };
      const { logTraceActions } = enums;
      const traceAction =
        logTraceActions[`latePaymentSlipEmail${fromHome ? 'Home' : ''}`];
      createTrace(traceAction, traceInfo);
    } else {
      console.error('Payment slip not ready', res);
    }
    return res;
  },
  async sendSlipBySms(
    { rootState },
    { item, cellphone, mode, fromHome = false }
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const contractId = item.contractId;

    let smsType = null;
    switch (mode) {
      case 'anticipation':
        smsType = 'anticipationSlipRequested';
        break;
      case 'late':
        smsType = 'lateSlipRequested';
        break;
      case 'toExpire':
        smsType = 'toExpireSlipRequested';
        break;
    }

    if (!smsType) {
      throw new Error('Invalid SMS type');
    }

    const data = {
      contractId,
      productId: selectedUnit.product.id,
      blockCode: selectedUnit.block.code,
      unitCode: selectedUnit.unit.code,
      documentCode: item.documentCode,
      smsType,
      userPhoneNumber: cellphone,
    };

    const url = process.env.SMS_API;
    return requestService(`${url}/addToQueue`, 'POST', {
      data,
      useLoading: false,
      localErrorHandling: true,
    })
      .then((res) => {
        const { count } = res;
        if (count) {
          const slipContract = selectedUnit.contracts.find(
            (contract) => contract.id === contractId
          );
          const traceInfo = {
            contractId: contractId,
            contractType: slipContract.type,
            detachedType: slipContract.detachedType,
            expirationDate: item.expirationDate,
            serieId: item.serieId,
            serieCode: item.serieCode,
            parcelCode: item.parcelCode,
            discountedValue: item.discountedValue,
            ready: item.ready,
            url: item.slipUrl,
            barcode: item.barcode,
            userCellphone: cellphone,
          };
          const { logTraceActions } = enums;
          const traceAction =
            logTraceActions[`latePaymentSlipSms${fromHome ? 'Home' : ''}`];
          createTrace(traceAction, traceInfo);
        }
        return res;
      })
      .catch((err) => {
        throw err;
      });
  },

  async getAnticipationSolicitations(
    { commit, rootState, state, rootGetters, dispatch },
    contracts = null
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const unitKey = selectedUnit ? selectedUnit.unit.id : null;

    if (
      state.anticipationSolicitationsCache.unitKey === unitKey &&
      state.anticipationSolicitationsCache.validUntil &&
      moment().isBefore(state.anticipationSolicitationsCache.validUntil)
    ) {
      return state.anticipationSolicitationsCache.data.items;
    }

    if (state.loadingSolicitations && state.anticipationSolicitationsPromise) {
      if (state.selectedUnitKey === unitKey) {
        return await state.anticipationSolicitationsPromise;
      }
      // Unidade mudou, ignora a Promise antiga e cria nova
    }

    state.selectedUnitKey = unitKey;

    state.anticipationSolicitationsPromise = (async () => {
      try {
        if (rootGetters[USER.GET_USER_ROLE] !== ROLES.CLIENT_ROLE) return;

        commit('SET_LOADING_SOLICITATIONS', true);

        if (!contracts) {
          if (!selectedUnit) return;
          const { ANTICIPATION_SLIPS } = enums.financialModules;
          contracts = await dispatch('getAllowedContracts', {
            module: ANTICIPATION_SLIPS,
            contracts: selectedUnit.contracts,
          });
        }

        const { sku } = selectedUnit;
        const params = {
          productSku: sku,
          contractIds: contracts.map((o) => o.id),
          contractTypes: contracts.map((o) => o.type),
          solicitationType: 'Anticipation',
        };
        const url = `${process.env.FINANCIAL_API_URL}/solicitation/query`;

        const { items, solicitationAllowed } = await requestService(
          url,
          'GET',
          {
            params,
            localErrorHandling: true,
          }
        );

        // Se unidade mudou durante requisição, ignora atualização de estado para evitar sobrescrever dados novos
        if (state.selectedUnitKey !== unitKey) {
          return;
        }

        commit('SET_DELAY_ALLOWED', solicitationAllowed);
        const cacheValidUntil = moment().add(2, 'minutes').toISOString();
        commit('SET_ANTICIPATION_SOLICITATIONS_CACHE', {
          unitKey,
          validUntil: cacheValidUntil,
          data: { items, solicitationAllowed },
        });

        if (items) {
          const logSolicitations = [];

          const readySolicitations = items.reduce((ready, o) => {
            if (o.ready) {
              const [item] = o.items;
              ready.push(item);
            }
            if (o.logError || o.logGenerated || o.logExpired) {
              logSolicitations.push(o);
            }
            return ready;
          }, []);

          dispatch('logSolicitations', {
            logSolicitations,
            selectedUnit,
            solicitationType: 'anticipation',
          });

          if (readySolicitations.length > 0) {
            commit(
              NOTIFICATIONS.MUTATE_ANTICIPATION_PAYMENT_SLIPS,
              readySolicitations,
              {
                root: true,
              }
            );
            state.hasAnticipationSolicitationsReady = readySolicitations.some(
              (item) => item.solicitationType === SOLICITATE_TYPE.ANTICIPATION
            );
          }

          return items;
        }
      } catch (err) {
        throw err;
      } finally {
        commit('SET_LOADING_SOLICITATIONS', false);
        state.anticipationSolicitationsPromise = null;
      }
    })();

    return await state.anticipationSolicitationsPromise;
  },

  async getAnticipationPaymentSlips(
    { commit, rootState, state, rootGetters, dispatch },
    contracts
  ) {
    const selectedUnit = rootState.unit.selectedUnit;
    const unitKey = selectedUnit ? selectedUnit.unit.id : null;

    if (
      state.anticipationSlipsCache.unitKey === unitKey &&
      state.anticipationSlipsCache.validUntil &&
      moment().isBefore(state.anticipationSlipsCache.validUntil)
    ) {
      return state.anticipationSlipsCache.data.items;
    }

    if (
      state.loadingItems &&
      state.anticipationPaymentSlipsPromise &&
      state.selectedUnitKey === unitKey
    ) {
      return await state.anticipationPaymentSlipsPromise;
    }

    state.selectedUnitKey = unitKey;
    state.anticipationPaymentSlipsPromise = (async () => {
      try {
        commit('SET_LOADING', true);
        if (!contracts) {
          if (!selectedUnit) return;
          const { ANTICIPATION_SLIPS } = enums.financialModules;
          contracts = await dispatch('getAllowedContracts', {
            module: ANTICIPATION_SLIPS,
            contracts: selectedUnit.contracts,
          });
        }

        const url = `${process.env.FINANCIAL_API_URL}/anticipation`;

        commit('SET_HAS_ANTICIPATION_SLIPS', false);

        const params = {
          contractIds: contracts.map((contract) => contract.id),
          contractTypes: contracts.map((contract) => contract.type),
        };

        const res = await requestService(url, 'GET', {
          params,
          localErrorHandling: true,
        });

        const anticipationSlipsByContract = {};
        if (res.hasLate) {
          commit('SET_HAS_LATE_SLIPS', true);
          commit('SET_HAS_LATE_ERROR', true);
        } else {
          for (const contract of res.items) {
            const contractId = contract.contractId;
            anticipationSlipsByContract[contractId] = [];

            const parcels = await Promise.all(
              contract.parcels.map(async (parcel) => {
                const discountObtained = await dispatch(
                  'calculateDiscountCampaign',
                  parcel
                );

                const slipValue = parcel.slipsUnion?.total ?? parcel.value;
                const paidValueDiscount = slipValue - discountObtained;

                return {
                  contractId,
                  paidValue: formatMoney(paidValueDiscount),
                  discount: discountObtained,
                  slipValue,
                  ...parcel,
                };
              })
            );

            anticipationSlipsByContract[contractId].push(...parcels);

            if (!state.hasAnticipationSlips)
              commit('SET_HAS_ANTICIPATION_SLIPS', true);
          }
        }
        const cacheValidUntil = moment().add(2, 'minutes').toISOString();
        const newCache = {
          unitKey,
          validUntil: cacheValidUntil,
          data: { items: { ...anticipationSlipsByContract } },
        };
        commit('SET_ANTICIPATION_SLIPS_CACHE', newCache);

        return anticipationSlipsByContract;
      } catch (err) {
        console.error('Error fetching anticipation payment slips:', err);
        throw err;
      } finally {
        commit('SET_LOADING', false);
        state.anticipationPaymentSlipsPromise = null;
      }
    })();

    return await state.anticipationPaymentSlipsPromise;
  },

  async calculateDiscountCampaign({ state }, parcel) {
    let result = 0.0;
    const isTypeValid = state.discountCampaign.allowedParcelTypes.some(
      (p) => p === parcel.type
    );
    const isFrequencyValid = state.discountCampaign.allowedFrequencyTypes.some(
      (p) => p === parcel.frequency
    );
    const proRataValue =
      state.discountCampaign.releaseProRata && parcel.proRata
        ? parcel.proRata
        : 0.0;

    if (isTypeValid && isFrequencyValid) {
      const additionalDiscount = parseFloat(
        (
          (parcel.discountedValue - proRataValue) *
          (state.discountCampaign.discountPercent / 100)
        ).toFixed(2)
      );
      result = additionalDiscount + proRataValue;
    }

    return result;
  },
  async getDiscountCampaign({ commit, state, rootState }) {
    const selectedUnit = rootState.unit.selectedUnit;
    if (!selectedUnit) return;
    const currentKey = `${selectedUnit.unit.id}-${selectedUnit.block.id}`;

    if (
      state.discountCampaignPromise &&
      state.discountCampaignPromiseKey === currentKey
    ) {
      return await state.discountCampaignPromise;
    }

    state.discountCampaignPromiseKey = currentKey;

    state.discountCampaignPromise = (async () => {
      try {
        let hasCampaign = false;
        let currCampaign = {
          hasDiscountCampaign: false,
          allowedParcelTypes: [],
          allowedFrequencyTypes: [],
          releaseProRata: false,
        };
        const today = moment().startOf('day').format('YYYY-MM-DD');

        const query = {
          active: true,
          productsById: {
            $in: [selectedUnit.product.id, '*'],
          },
          $nor: [{ validTo: { $lt: today } }, { validFrom: { $gt: today } }],
        };

        const resultDiscountCampaign = await requestService(
          `${process.env.CRUD_API_GET}/discountCampaign`,
          'GET',
          {
            params: { filter: { query } },
            localErrorHandling: true,
          }
        );

        if (!resultDiscountCampaign.items.length) {
          return currCampaign;
        }

        campaign = resultDiscountCampaign.items[0];

        hasCampaign = true;

        if (campaign.productsById.indexOf('*') === -1) {
          if (campaign.groups.length) {
            const resultGroup = await requestService(
              `${process.env.CRUD_API_GET}/group`,
              'GET',
              { localErrorHandling: true }
            );

            if (resultGroup.items.length) {
              resultGroup.items.forEach((g) =>
                campaign.products.push(...g.products)
              );
            }
          }

          if (!campaign.products.some((p) => p.id === '*')) {
            const productsCampaign = campaign.products.filter(
              (p) => parseInt(p.id) === selectedUnit.product.id
            );
            const blocksCampaign = productsCampaign.map((p) => p.blocks).flat();
            const blockIds = blocksCampaign.map((b) => parseInt(b.id));

            if (blockIds.length) {
              hasCampaign = blockIds.some((id) => id === selectedUnit.block.id);
            }
          }
        }

        // Se selectedUnit mudou desde o início, ignora atualização
        if (state.discountCampaignPromiseKey !== currentKey) {
          return;
        }

        if (hasCampaign) {
          currCampaign = {
            hasDiscountCampaign: hasCampaign,
            allowedParcelTypes: campaign.parcelTypes,
            allowedFrequencyTypes: campaign.frequencyTypes,
            discountPercent: campaign.discount,
            releaseProRata: campaign.releaseProRata,
          };
        }
        commit('SET_DISCOUNT_CAMPAIGN', currCampaign);
        return currCampaign;
      } catch (err) {
        state.discountCampaignPromise = null;
        state.discountCampaignPromiseKey = null;
        throw err;
      }
    })();

    return await state.discountCampaignPromise;
  },

  resetAnticipationSlipsCache({ commit, dispatch }) {
    commit('CLEAR_ANTICIPATION_SLIPS_CACHE');
    commit('CLEAR_ANTICIPATION_SOLICITATIONS_CACHE');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/paymentSlips/solicitations.js